import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTray, toggleTray, clearTray, compareHref, compareVariants } from '../lib/compareTray';
import { MAX_COMPARE } from '../data/compare';
import { IMG_FALLBACK } from '../lib/images';
import { formatINR } from '../lib/quote';

// The compare tray, PolicyBazaar style (Oct 2026). Tick "Compare" on any card
// and this rises from the bottom of the shop and collection pages:
//
//   - a header with how many are picked, Clear all, and a button to tuck the
//     tray away while browsing;
//   - one slot per pick (photo, size and rating, price, remove), "vs"
//     between them, and dashed slots for the rest, up to four;
//   - "Compare now" once there are two. Until then it says what to do.
//
// Phones get a compact version: small photos and the button, in one row.

const X = ({ className = 'h-3.5 w-3.5' }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
  </svg>
);

function label(v) {
  const m = v.model;
  if (m.soldAs && m.stars) {
    return {
      line1: `${m.soldAs} Ton ${m.stars} Star`,
      line2: `${m.inverter === false ? 'Fixed speed' : 'Inverter'}${v.kit === 'withKit' ? ' · with kit' : ''}`,
    };
  }
  return { line1: (m.title || '').replace(/^Daikin /, '').replace(/ Split AC$/, ''), line2: v.clearance ? 'Clearance' : '' };
}

export default function CompareBar() {
  const ids = useTray();
  const [tucked, setTucked] = useState(false);
  const byId = useMemo(() => new Map(compareVariants().map((v) => [v.id, v])), []);
  if (!ids.length) return null;

  const picked = ids.map((id) => byId.get(id)).filter(Boolean);
  const n = picked.length;
  const ready = n >= 2;
  const slots = [...picked, ...Array(Math.max(0, MAX_COMPARE - n)).fill(null)];
  const hint = ready ? `${n} ACs ready to compare` : 'Pick one more AC to compare';

  const Button = ({ className = '' }) => (ready ? (
    <Link to={compareHref(ids)} data-testid="compare-bar-link"
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 font-semibold text-white shadow-sm transition hover:bg-blue-700 ${className}`}>
      Compare now
      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 10h11M11 5.5l4.5 4.5-4.5 4.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </Link>
  ) : (
    <span className={`inline-flex items-center justify-center rounded-lg bg-gray-100 font-semibold text-gray-400 ${className}`}>
      Compare now
    </span>
  ));

  // Tucked away: one small pill, bottom centre.
  if (tucked) {
    return (
      <button type="button" onClick={() => setTucked(false)} data-testid="compare-bar"
        className="fixed bottom-4 left-1/2 z-40 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:bg-gray-800">
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-500 px-1.5 text-xs">{n}</span>
        Compare ACs
        <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12.5l5-5 5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
    );
  }

  return (
    <>
    {/* Room at the end of the page, so the tray never hides the last row. */}
    <div aria-hidden="true" className="h-36 sm:h-40" />
    <div className="fixed inset-x-0 bottom-0 z-40 animate-in slide-in-from-bottom-4 fade-in duration-200" data-testid="compare-bar">
      <div className="mx-auto max-w-6xl sm:px-4">
        <div className="rounded-t-2xl border border-b-0 border-gray-200 bg-white shadow-[0_-8px_30px_rgba(15,23,42,0.12)]">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 sm:px-4">
            <p className="min-w-0 text-sm">
              <span className="font-semibold text-gray-900">Compare ACs</span>
              <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">{n} of {MAX_COMPARE}</span>
              <span className="ml-2 hidden text-xs text-gray-500 sm:inline">{hint}</span>
            </p>
            <div className="flex shrink-0 items-center gap-1">
              <button type="button" onClick={clearTray} data-testid="compare-bar-clear"
                className="rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                Clear all
              </button>
              <button type="button" onClick={() => setTucked(true)} aria-label="Hide compare tray"
                className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 7.5l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
          </div>

          {/* Desktop and tablet: four slots with "vs" between, then the button. */}
          <div className="hidden items-stretch gap-3 px-4 py-3 sm:flex">
            <div className="grid min-w-0 flex-1 grid-cols-4 gap-3">
              {slots.map((v, i) => (
                <div key={v ? v.id : `empty-${i}`} className="relative min-w-0">
                  {i > 0 && (
                    <span aria-hidden="true" className="absolute top-1/2 z-10 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-[10px] font-bold uppercase text-gray-500 shadow-sm"
                      style={{ left: '-0.375rem' }}>
                      vs
                    </span>
                  )}
                  {v ? (
                    <div className="flex h-full items-center gap-2.5 rounded-xl border border-gray-200 bg-white p-2 pr-7">
                      <img src={v.model.image || IMG_FALLBACK} alt="" className="h-12 w-16 shrink-0 rounded-md bg-gray-50 object-contain p-1" />
                      <span className="min-w-0">
                        <span className="block truncate text-[13px] font-semibold text-gray-900">{label(v).line1}</span>
                        <span className="block truncate text-[11px] text-gray-500">{label(v).line2}</span>
                        <span className="block text-[13px] font-semibold text-gray-900">{formatINR(v.price)}</span>
                      </span>
                      <button type="button" onClick={() => toggleTray(v.id)} aria-label={`Remove ${label(v).line1} from compare`}
                        className="absolute right-1.5 top-1.5 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                        <X />
                      </button>
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[68px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/60 px-2 text-center">
                      <span className="text-lg leading-none text-gray-300">+</span>
                      <span className="mt-0.5 text-[11px] text-gray-400">Tick Compare on any AC</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex w-40 shrink-0 flex-col justify-center">
              <Button className="h-11 w-full text-sm" />
              {!ready && <p className="mt-1 text-center text-[11px] text-gray-500">Add one more AC</p>}
            </div>
          </div>

          {/* Phones: photos in a row, then the button. */}
          <div className="flex items-center gap-2 px-3 py-2.5 sm:hidden">
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              {slots.map((v, i) => (v ? (
                <span key={v.id} className="relative h-11 w-11 shrink-0 rounded-lg border border-gray-200 bg-gray-50">
                  <img src={v.model.image || IMG_FALLBACK} alt={label(v).line1} className="h-full w-full object-contain p-1" />
                  <button type="button" onClick={() => toggleTray(v.id)} aria-label={`Remove ${label(v).line1} from compare`}
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-700 text-white">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ) : (
                <span key={`empty-${i}`} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-gray-200 text-gray-300">+</span>
              )))}
            </div>
            <Button className="h-11 shrink-0 px-4 text-sm" />
          </div>
          {!ready && <p className="-mt-1 px-3 pb-2 text-[11px] text-gray-500 sm:hidden">Pick one more AC to compare</p>}
        </div>
      </div>
    </div>
    </>
  );
}
